<template>
  <property-report-card
    title="Rent Comparison"
    description="Compare asking and effective rents across multiple market metrics."
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <template #actions>
      <q-select
        v-model="groupBy"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Group By"
        :options="groupByOptions"
        @update:model-value="(ut) => updateGroupBy(ut)"
      />
      <q-select
        v-model="colorBy"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Bubble Color"
        :options="[
          {
            label: 'Property',
            value: 'property'
          },
          {
            label: 'Unit Type',
            value: 'unitType'
          }
        ]"
        @update:model-value="(ut) => $tracker.changedFilter('color_by', 'bubble_chart', ut)"
      />
      <q-select
        v-model="bubbleRadius"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Bubble Sizes"
        :options="[
          {
            label: 'Count',
            value: 'count'
          },
          {
            label: 'Days on Market',
            value: 'timeOnMarket'
          }
        ]"
        @update:model-value="(ut) => $tracker.changedFilter('bubble_size', 'bubble_chart', ut)"
      />
      <q-select
        v-model="metric"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Metric"
        :options="[
          {
            label: 'Rent PSF',
            value: 'rentPsf'
          },
          {
            label: 'Rent',
            value: 'rent'
          }
        ]"
        @update:model-value="(ut) => $tracker.changedFilter('metric', 'bubble_chart', ut)"
      />
      <q-select
        v-model="rentType"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Rent Type"
        :options="[
          {
            label: 'Asking',
            value: 'asking'
          },
          {
            label: 'Effective',
            value: 'effective'
          }
        ]"
        @update:model-value="(ut) => $tracker.changedFilter('rent_type', 'bubble_chart', ut)"
      />
    </template>
    <q-separator />
    <q-card-section>
      <div id="intercom-rent-comparison-content" style="overflow-x: auto">
        <div style="width: 100%; height: 100%; height: 300px">
          <bubble :data="data" :options="options" :plugins="chartPlugins" />
        </div>
      </div>
    </q-card-section>
    <q-separator v-if="!printingMode" />
    <q-card-section v-if="!printingMode">
      <q-card flat>
        <div v-if="userSelection && tableUnits.length" class="flex justify-between q-mb-md">
          <div class="text-bold q-mb-md q-my-auto">
            {{ formatBed(userSelection.bed) }} Units in {{ userSelection.property }}
            <q-icon name="close" class="q-ml-md cursor-pointer" @click="closePanel" />
          </div>
          <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <div
          v-if="!tableUnits.length"
          class="full-width text-center justify-center flex align-center"
          data-html2canvas-ignore
        >
          <q-icon name="info" size="18px" color="accent" class="q-mr-sm q-my-auto" />
          Click on a bubble on the chart above to see the units that make it up.
        </div>
        <unit-details-table
          v-else
          :rows="tableUnits"
          :expanded-unit-history="expandedUnitHistory"
          :date-range="dateRange"
        />
      </q-card>
    </q-card-section>
  </property-report-card>
</template>

<script lang="ts">
import { type LegendItem } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bubble } from 'vue-chartjs'
import { type PropType } from 'vue'
import type { ExpandedUnitHistory } from '@/types/propertyReports'
import {
  type PropertyDetailsResponse,
  type Availability,
  type Price,
  type AvailabilityPeriod,
  daysOnMarket
} from 'common'
import { unitTypeColor, avg, unitsFound } from '@/utils'
import UnitDetailsTable, { exportCSV } from './UnitDetailsTable.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

const formatBed = (bed: number) => (bed === 0 ? 'Studio' : `${bed} Bed`)

const formatType = (u: Availability) => {
  // return 1BR 2.5BA, etc
  if (u.bed === 0) {
    return 'Studio'
  }
  return `${u.bed!}BR ${u.bath!}BA`
}

const propertyColors = [
  '#F80',
  'rgba(255, 193, 7, 1)',
  'rgba(255, 235, 59, 1)',
  'rgba(255, 87, 34, 1)',
  'rgba(3, 169, 244, 1)',
  'rgba(233, 30, 99, 1)',
  'rgba(76, 175, 80, 1)',
  'rgba(156, 39, 176, 1)',
  'rgba(139, 195, 74, 1)',
  'rgba(0, 188, 212, 1)',
  'rgba(22, 191, 195, 1)',
  'rgba(40, 146, 220, 1)'
]

export default {
  name: 'BubbleChart',
  components: { PropertyReportCard, Bubble, UnitDetailsTable },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<ExpandedUnitHistory>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    hideNumbers: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartPlugins: [ChartDataLabels],
      userSelection: null as { propertyId: string; property: string; bed: number } | null,
      groupBy: {
        label: 'Type',
        value: 'propertyType'
      } as {
        label: string
        value: 'property' | 'propertyType' | 'propertyBed' | 'propertyFloorplan' | 'propertyUnit'
      },
      groupByOptions: [
        {
          label: 'Property',
          value: 'property'
        },
        {
          label: 'Bed',
          value: 'propertyBed'
        },
        {
          label: 'Type',
          value: 'propertyType'
        },
        {
          label: 'Floorplan',
          value: 'propertyFloorplan'
        },
        {
          label: 'Unit',
          value: 'propertyUnit'
        }
      ],
      bubbleRadius: {
        label: 'Count',
        value: 'count'
      } as {
        label: string
        value: 'count' | 'timeOnMarket'
      },
      metric: {
        label: 'Rent',
        value: 'rent'
      } as {
        label: string
        value: 'rentPsf' | 'rent'
      },
      colorBy: {
        label: 'Unit Type',
        value: 'unitType'
      } as {
        label: string
        value: 'property' | 'unitType'
      },
      rentType: {
        label: 'Asking',
        value: 'asking'
      } as {
        label: string
        value: 'asking' | 'effective'
      }
    }
  },
  computed: {
    subjectVisible() {
      return this.filteredProperties.some((_) => _.prop.id === this.subjectDetail?.id)
    },
    tableUnits() {
      const selection = this.userSelection
      if (selection === null) {
        return []
      }
      const ret = this.validUnits.filter(
        (unit) => unit.unit.bed === selection.bed && unit.prop.id === selection.propertyId
      )
      return ret
    },
    validUnits() {
      return this.filteredProperties.flatMap(({ prop, units }) =>
        units.map(({ unit, lastHistory, period }) => ({
          unit,
          lastHistory,
          period,
          prop
        }))
      )
    },
    bubbleGroupKey(): (arg0: { prop: PropertyDetailsResponse; unit: Availability }) => string {
      switch (this.groupBy.value) {
        case 'property':
          return ({ prop }: { prop: PropertyDetailsResponse; unit: Availability }) => {
            return prop.building_name || prop.street_address
          }
        case 'propertyType':
          return ({ prop, unit }: { prop: PropertyDetailsResponse; unit: Availability }) => {
            return `${prop.building_name || prop.street_address} - ${formatType(unit)}`
          }
        case 'propertyBed':
          return ({ prop, unit }: { prop: PropertyDetailsResponse; unit: Availability }) => {
            return `${prop.building_name || prop.street_address} - ${formatBed(unit.bed)}`
          }
        case 'propertyFloorplan':
          return ({ prop, unit }: { prop: PropertyDetailsResponse; unit: Availability }) => {
            return `${prop.building_name || prop.street_address} - ${unit.floorplan_name || formatType(unit)}`
          }
        case 'propertyUnit':
          return ({ prop, unit }: { prop: PropertyDetailsResponse; unit: Availability }) => {
            return `${prop.building_name || prop.street_address} - ${unit.unit_name || formatType(unit)}`
          }
        default:
          throw new Error('Unknown group by')
      }
    },
    unitsData() {
      const groupByFct = this.bubbleGroupKey

      return this.filteredProperties.flatMap(({ prop, units }) => {
        // we create one dot for each {property, bed} combination, computing the average sqft and Rent
        const aggregated: Record<
          string,
          {
            bed: number
            sqft: number | null
            rent: number | null
            daysOnMarket: number | null
            rentPsf: number | null
            effectiveRent: number | null
            effectiveRentPsf: number | null
          }[]
        > = {}

        for (const { unit, lastHistory, period } of units) {
          const sqft = unit.sqft || unit.min_sqft
          const key = groupByFct({ prop, unit })
          if (!aggregated[key]) {
            aggregated[key] = []
          }
          const price = (lastHistory.price ?? lastHistory.min_price) as number
          const effPrice = (lastHistory.effective_price ??
            lastHistory.min_effective_price) as number
          aggregated[key].push({
            bed: unit.bed!,
            sqft: sqft,
            rent: price,
            daysOnMarket: daysOnMarket(period, this.dateRange.to),
            rentPsf: sqft ? price / sqft : null,
            effectiveRent: effPrice,
            effectiveRentPsf: sqft ? effPrice / sqft : null
          })
        }

        // we compute the averages for each {property, key} combination.
        return Object.entries(aggregated).flatMap(([key, data]) => ({
          key,
          propertyId: prop.id,
          property: prop.building_name || prop.street_address,
          bed: data[0].bed,
          sqft: avg(data.map((_) => _.sqft)),
          rent: avg(data.map((_) => _.rent)),
          avgDaysOnMarket: avg(data.map((_) => _.daysOnMarket)),
          rentPsf: avg(data.map((_) => _.rentPsf)),
          effectiveRent: avg(data.map((_) => _.effectiveRent)),
          effectiveRentPsf: avg(data.map((_) => _.effectiveRentPsf)),
          count: data.length
        }))
      })
    },
    data() {
      // we define the getter for the x and y axis
      const getY = () => {
        switch (this.metric.value) {
          case 'rent':
            return this.rentType.value === 'asking'
              ? (u: { rent: number | null }) => u.rent
              : (u: { effectiveRent: number | null }) => u.effectiveRent
          case 'rentPsf':
            return this.rentType.value === 'asking'
              ? (u: { rentPsf: number | null }) => u.rentPsf
              : (u: { effectiveRentPsf: number | null }) => u.effectiveRentPsf
        }
      }

      const getColor = () => {
        const propertyIndixes = this.filteredProperties.map(({ prop }) => prop.id)
        switch (this.colorBy.value) {
          case 'property':
            return (u: { propertyId: string }) =>
              propertyColors[propertyIndixes.indexOf(u.propertyId) % propertyColors.length]
          case 'unitType':
            return (u: { bed: number }) => unitTypeColor(u.bed)
        }
      }

      // we group the data either by property or by unit type
      const groupedData = this.unitsData.reduce(
        (acc, dot) => {
          const key = this.colorBy.value === 'property' ? dot.property : formatBed(dot.bed)
          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(dot)
          return acc
        },
        {} as Record<string | number, typeof this.unitsData>
      )

      const yGetter = getY()
      const colorGetter = getColor()

      const allCounts = Object.values(groupedData).flatMap((_) => _.map((_) => _.count))
      const minCount: number = Math.min(...allCounts)
      const maxCount: number = Math.max(...allCounts)

      const allTimesOnMarket = Object.values(groupedData)
        .flatMap((_) => _.map((_) => _.avgDaysOnMarket))
        .filter((_) => _ !== null) as number[]
      const minTimeOnMarket: number = allTimesOnMarket.length ? Math.min(...allTimesOnMarket) : 1
      const maxTimeOnMarket: number = allTimesOnMarket.length ? Math.max(...allTimesOnMarket) : 1

      const radiusCoeff = (
        unit: { count: number; avgDaysOnMarket: number | null },
        minRadius: number = 8,
        extraRadius: number = 20
      ) => {
        if (this.bubbleRadius.value === 'count') {
          return (
            minRadius + extraRadius * ((unit.count - minCount) / ((maxCount - minCount + 1) * 1.0))
          )
        }
        if (this.bubbleRadius.value === 'timeOnMarket' && unit.avgDaysOnMarket !== null) {
          return (
            minRadius +
            extraRadius *
              ((unit.avgDaysOnMarket - minTimeOnMarket) /
                ((maxTimeOnMarket - minTimeOnMarket) * 1.0))
          )
        }
        return 5
      }

      return {
        datasets: Object.entries(groupedData).map(([key, data]) => ({
          label: key,
          backgroundColor: colorGetter(data[0]),
          data: data.map((unit) => ({
            key: unit.key,
            label:
              this.printingMode || this.hideNumbers
                ? ''
                : this.subjectDetail?.id === unit.propertyId
                  ? '★'
                  : (
                      this.filteredProperties.findIndex(({ prop }) => prop.id === unit.propertyId) +
                      (this.subjectVisible ? 0 : 1)
                    ).toString(),
            propertyId: unit.propertyId,
            property: unit.property,
            bed: unit.bed,
            count: unit.count,
            avgDaysOnMarket: unit.avgDaysOnMarket,
            x: unit.sqft,
            y: yGetter(unit),
            r: radiusCoeff(unit)
          }))
        }))
      }
    },
    options() {
      const orderLegend = ['Studio', '1 Bed', '2 Bed', '3 Bed', '4 Bed', '5 Bed', '6 Bed']
      return {
        onClick: (_: unknown, clickedElements: { datasetIndex: number; index: number }[]) => {
          if (clickedElements.length === 0) {
            return
          }
          const relevantDatapoints =
            this.data.datasets[clickedElements[0].datasetIndex].data[clickedElements[0].index]
          this.userSelection = {
            propertyId: relevantDatapoints.propertyId,
            property: relevantDatapoints.property,
            bed: relevantDatapoints.bed
          }
          this.$tracker.clickedOnGraph('bubble_chart')
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Sqft'
            },
            type: 'linear',
            position: 'bottom'
          },
          y: {
            title: {
              display: true,
              text: this.metric.label
            },
            type: 'linear',
            position: 'left'
          }
        },
        plugins: {
          datalabels: {
            color: 'white',
            font: { weight: 'bold' }
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 14
              },
              sort: (a: LegendItem, b: LegendItem) => {
                return orderLegend.indexOf(a.text) - orderLegend.indexOf(b.text)
              }
            }
          },
          tooltip: {
            callbacks: {
              title: (context: { raw: { key: string }[] }) => {
                return context[0].raw.key
              },
              label: (context: {
                raw: { x: number; y: number; count: number; avgDaysOnMarket: number }
              }) => {
                const digits = this.metric.value === 'rent' ? 0 : 2
                const roundedY = context.raw.y.toFixed(digits)
                return `${context.raw.x.toFixed(0)} sqft - ${this.metric.value === 'rent' ? '$' : ''}${roundedY} ${this.metric.value === 'rent' ? '' : 'psf'}`
              },
              afterLabel: (context: { raw: { count: number; avgDaysOnMarket: number } }) => {
                if (this.bubbleRadius.value === 'timeOnMarket') {
                  return `Avg Days on Market: ${context.raw.avgDaysOnMarket.toFixed(0)}`
                } else {
                  return `Count: ${context.raw.count}`
                }
              }
            }
          }
        }
      }
    }
  },
  watch: {
    dateRange() {
      this.userSelection = null
    }
  },
  methods: {
    unitsFound,
    formatBed,
    updateGroupBy(newGroup: { label: string; value: string }) {
      this.$tracker.changedFilter('group_by', 'bubble_chart', newGroup)
      if (newGroup.value === 'property') {
        this.colorBy = {
          label: 'Property',
          value: 'property'
        }
      }
    },
    closePanel() {
      this.userSelection = null
    },
    exportCSV() {
      exportCSV(this.tableUnits, 'bubble-chart', this.dateRange)
      this.$tracker.exported('bubble_chart')
    }
  }
}
</script>
